/**
 * Edit existing community listing (author only).
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { communityListingsService } from '../services/communityListingsService';
import { YButton } from '../../../ui/YButton';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { CommunityListingPageShell } from './CommunityListingPageShell';
import { CommunityListingFormFields } from './CommunityListingFormFields';
import './community-listing-pages.css';

export function CommunityListingEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    price: '',
    condition: '',
    location_text: '',
    latitude: null,
    longitude: null,
  });

  useEffect(() => {
    let cancelled = false;
    communityListingsService
      .get(id)
      .then((data) => {
        if (!cancelled) {
          setListing(data);
          setForm({
            title: data.title || '',
            category: data.category || '',
            description: data.description || '',
            price: data.price != null && data.price !== '' ? String(data.price) : '',
            condition: data.condition || '',
            location_text: data.location_text || '',
            latitude: data.latitude != null ? data.latitude : null,
            longitude: data.longitude != null ? data.longitude : null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setError('Listing not found.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category) {
      setError('Title and category are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        description: (form.description || '').trim(),
        condition: (form.condition || '').trim() || undefined,
        location_text: (form.location_text || '').trim() || undefined,
      };
      if (form.latitude != null && form.longitude != null) {
        payload.latitude = form.latitude;
        payload.longitude = form.longitude;
      }
      if (form.price !== '' && form.price != null) {
        const p = parseFloat(form.price);
        if (!Number.isNaN(p) && p >= 0) payload.price = String(p);
      }
      await communityListingsService.update(id, payload);
      navigate(`/camp-essentials/gear/listings/${id}`, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.title?.[0] ||
        'Could not update listing.';
      setError(typeof msg === 'string' ? msg : 'Could not update listing.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CommunityListingPageShell title="Edit listing" subtitle="Marketplace" testId="community-listing-edit">
        <p className="community-listing-page__loading">Loading…</p>
      </CommunityListingPageShell>
    );
  }

  if (error && !listing) {
    return (
      <CommunityListingPageShell title="Edit listing" subtitle="Marketplace" testId="community-listing-edit">
        <p className="community-listing-page__error">{error}</p>
      </CommunityListingPageShell>
    );
  }

  const isAuthor = currentUser?.id != null && Number(listing.author_id) === Number(currentUser.id);
  if (!isAuthor) {
    return (
      <CommunityListingPageShell title="Edit listing" subtitle="Marketplace" testId="community-listing-edit">
        <p className="community-listing-page__error">You can only edit your own listing.</p>
      </CommunityListingPageShell>
    );
  }

  return (
    <CommunityListingPageShell
      title="Edit listing"
      subtitle={listing.title}
      backLabel="Back to listing"
      backTo={`/camp-essentials/gear/listings/${id}`}
      testId="community-listing-edit"
    >
      <form onSubmit={handleSubmit}>
        <CommunityListingFormFields
          form={form}
          onFieldChange={handleFieldChange}
          onLocationTextChange={(v) =>
            setForm((prev) => ({ ...prev, location_text: (v || '').slice(0, 120) }))
          }
          onLocationSelect={(place) =>
            setForm((prev) => ({
              ...prev,
              location_text: (place.label || place.name || '').slice(0, 120),
              latitude: place.latitude != null ? place.latitude : null,
              longitude: place.longitude != null ? place.longitude : null,
            }))
          }
        />

        {error ? <div className="community-listing-form__error">{error}</div> : null}

        <div className="community-listing-form__actions">
          <YButton type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </YButton>
          <YButton
            type="button"
            variant="outline-secondary"
            onClick={() => navigate(`/camp-essentials/gear/listings/${id}`)}
          >
            Cancel
          </YButton>
        </div>
      </form>
    </CommunityListingPageShell>
  );
}
